import React from "react";
import { Phone, Mail, BadgeCheck, ArrowRight } from "lucide-react";
import { usePageMeta } from "@/components/site/usePageMeta";
import { PageHero } from "@/components/site/PageHero";
import { SectionHeading, Btn } from "@/components/site/ui";
import { Reveal } from "@/components/site/useReveal";
import { WhySection } from "@/components/site/WhySection";
import { Testimonials } from "@/components/site/Testimonials";
import { ContactSection } from "@/components/site/ContactSection";
import { CONTACT, ROUTES } from "@/components/site/data";

const IMG = "https://images.pexels.com/photos/7578939/pexels-photo-7578939.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1000";

export default function AgentsPage() {
  usePageMeta(
    "Our Agents | Wallstreet Realty",
          "Meet the Wallstreet Realty team, broker-led representation for Southern California buyers and sellers, headed by broker John Pagani, DRE #01926616."
  );
  return (
    <>
      <PageHero
        crumb="Agents"
        eyebrow="Meet The Team"
        title="Broker-Led. Client-First."
                subtitle="Every Wallstreet Realty client works with an agent backed directly by the broker, real accountability, local knowledge, and mortgage fluency on every transaction."
        image={IMG}
        ctas={[
          { label: "Work With Us", href: ROUTES.contact, testid: "agents-hero-contact" },
          { label: "Join the Team", href: ROUTES.join, variant: "gold", testid: "agents-hero-join" },
        ]}
      />

      {/* Broker card */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Leadership" title="Meet the Broker" center />
          <Reveal>
            <div className="mt-10 bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-8 sm:p-12 grid lg:grid-cols-[auto,1fr] gap-8 lg:gap-12 items-center max-w-4xl mx-auto">
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-[#254369] flex items-center justify-center mx-auto lg:mx-0">
                <span className="font-heading font-extrabold text-4xl sm:text-5xl text-white">JP</span>
              </div>
              <div className="text-center lg:text-left">
                <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#2D2D37]">John Pagani</h3>
                <p className="mt-1 text-[#C9A961] font-heading font-bold uppercase tracking-[0.15em] text-xs">
                  Broker / CEO · {CONTACT.dre}
                </p>
                <p className="mt-4 text-[15px] leading-relaxed text-[#2D2D37]/70 max-w-xl">
                  John leads Wallstreet Realty as a hands-on broker across both
                                    real estate and mortgage, which means clients get pricing
                  strategy and financing strategy in the same conversation, and
                  agents get a broker who actually picks up the phone. Licensed
                  through CRMLS and serving Los Angeles County, Orange County,
                  and greater Southern California.
                </p>
                <div className="mt-6 flex flex-wrap justify-center lg:justify-start gap-3">
                  <a
                    href={`tel:${CONTACT.phoneTel}`}
                    data-testid="agents-broker-call"
                    className="inline-flex items-center gap-2 bg-[#254369] text-white rounded-lg px-5 py-2.5 font-medium hover:bg-[#2D2D37] transition-colors"
                  >
                    <Phone size={16} /> {CONTACT.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="agents-broker-email"
                    className="inline-flex items-center gap-2 border-2 border-[#254369] text-[#254369] rounded-lg px-5 py-2.5 font-medium hover:bg-white transition-colors"
                  >
                    <Mail size={16} /> Email John
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="mt-8 max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
              {[
                { t: "Real Estate + Mortgage", d: "One brokerage, both sides of the transaction." },
                { t: "CRMLS Member", d: "Full MLS access across Southern California." },
                { t: "Agent-First Brokerage", d: "A growing team of local, supported agents." },
              ].map((b) => (
                <div key={b.t} className="bg-white border border-[#E5E5E5] rounded-[20px] p-6 text-center">
                  <BadgeCheck className="mx-auto text-[#C9A961]" size={22} />
                  <p className="mt-3 font-heading font-bold text-[15px] text-[#2D2D37]">{b.t}</p>
                  <p className="mt-1 text-[13px] text-[#2D2D37]/60 leading-relaxed">{b.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <div className="mt-10 max-w-2xl mx-auto text-center bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-8"><p className="font-heading font-bold text-lg text-[#2D2D37]">IDX Home Search for Every Agent</p><p className="mt-2 text-sm text-[#2D2D37]/65">Every Wallstreet Realty agent gets a branded IDX home search to share with clients and generate leads.</p><div className="mt-5"><Btn href={ROUTES.idx} variant="outline" testid="agents-idx-demo">See the IDX Demo</Btn></div>
        </div>
        </div>
      </section>

      {/* Growing team → recruiting bridge */}
      <section className="py-16 lg:py-20 bg-[#254369]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-white">
            Our Roster Is Growing
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed max-w-2xl mx-auto">
            Wallstreet Realty is actively adding licensed Southern California
            agents who want broker support and a commission model that respects
            their production. Your profile could be on this page.
          </p>
          <div className="mt-8">
            <Btn href={ROUTES.join} variant="gold" testid="agents-join-cta">
              See Why Agents Join Us <ArrowRight size={16} />
            </Btn>
          </div>
        </div>
      </section>

      <WhySection />
      <Testimonials />
      <ContactSection />
    </>
  );
}
